// ============================================================
// Toast.jsx
// Minimal single-instance toast (Session 4b). Controlled by the parent:
// the parent holds one toast state object and renders ONE <Toast>; this
// component renders nothing when `message` is empty.
//
//   <Toast message="Saved" variant="success" onDismiss={() => setToast(null)} />
//
// - Two variants: 'success' (role="status", polite, 3s) / 'error'
//   (role="alert", assertive, 5s).
// - Auto-dismiss only — NO tap-to-dismiss in v1.
// - Plain CSS transition; prefers-reduced-motion honoured in index.css.
// - No new dependency.
//
// Rendered via createPortal to document.body (NOT into the screen tree).
// A position:fixed element nested inside an iOS momentum-scroll container
// (-webkit-overflow-scrolling:touch) or any transformed/contained ancestor
// is NOT viewport-fixed on the Teams WKWebView — it's trapped by that
// ancestor and painted behind the bottom nav. document.body is the only
// ancestor guaranteed not to establish a containing block for fixed.
// See CLAUDE.md Gotcha #85.
// ============================================================

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { announce } from './a11y/LiveAnnouncer';
// Toast styles live in src/index.css (the always-first global stylesheet) —
// promoted there 2026-06-17 (sub-phase 2d-1) so the shared Toast component
// can be used on both the carer and manager surfaces without Toast.css
// spanning two webpack chunks and tripping mini-css-extract's "Conflicting
// order" rule (a hard build failure under CI=true).

const DISMISS_MS = { success: 3000, error: 5000 };

export default function Toast({ message, variant = 'success', onDismiss }) {
  // Also push through the persistent live region. The role/aria-live
  // below is correct and is what an auditor looks for, but this
  // element is conditionally rendered — it enters the DOM already
  // carrying its text, and a live region with no prior empty state is
  // not reliably announced. The persistent region has no such problem.
  // A toast is the ONLY feedback for most saves, so a missed
  // announcement means a blind user never learns the save happened.
  // See components/a11y/LiveAnnouncer.jsx.
  useEffect(() => {
    if (!message) return;
    announce(message, variant === 'error' ? 'assertive' : 'polite');
  }, [message, variant]);

  useEffect(() => {
    if (!message) return undefined;
    const ms = DISMISS_MS[variant] || DISMISS_MS.success;
    const t = setTimeout(() => { if (onDismiss) onDismiss(); }, ms);
    return () => clearTimeout(t);
  }, [message, variant, onDismiss]);

  if (!message) return null;

  // Portal to document.body so the fixed position is viewport-relative,
  // not trapped by the carer sub-screen's momentum-scroll container.
  return createPortal(
    <div
      className={`toast toast--${variant}`}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
    >
      {message}
    </div>,
    document.body
  );
}
